/**
 * Applications Manager Component
 * Allows companies to review and manage job applications
 */

import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  User, 
  Calendar, 
  MapPin, 
  Mail, 
  Phone,
  GraduationCap,
  Download,
  Eye,
  Check,
  X,
  Clock,
  Filter,
  Search,
  ChevronDown,
  ChevronUp,
  Star,
  MessageCircle,
  Briefcase,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { useCompanyAuth } from '../../../core/contexts/CompanyAuthContext';
import { 
  getCompanyApplications, 
  updateApplicationStatus,
  searchCompanyApplications 
} from '../services/companyService';

/**
 * Applications Manager Component
 * 
 * @returns {JSX.Element} Applications manager
 */
const ApplicationsManager = () => {
  const { companyProfile } = useCompanyAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    jobId: '',
    searchTerm: '',
    university: '',
    dateRange: null
  });
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    reviewing: 0,
    accepted: 0,
    rejected: 0
  });

  // Application statuses
  const applicationStatuses = [
    { value: 'pending', label: 'Pending Review', color: 'yellow', icon: Clock },
    { value: 'reviewing', label: 'Under Review', color: 'blue', icon: Eye },
    { value: 'accepted', label: 'Accepted', color: 'green', icon: CheckCircle },
    { value: 'rejected', label: 'Rejected', color: 'red', icon: XCircle },
    { value: 'withdrawn', label: 'Withdrawn', color: 'gray', icon: X }
  ];

  // Load applications on component mount
  useEffect(() => {
    if (companyProfile) {
      loadApplications();
    }
  }, [companyProfile, filters]);

  // Load applications
  const loadApplications = async () => {
    try {
      setLoading(true);
      
      let applicationsData;
      if (filters.searchTerm || filters.university || filters.dateRange) {
        applicationsData = await searchCompanyApplications(companyProfile.id, filters);
      } else {
        const result = await getCompanyApplications(companyProfile.id, {
          status: filters.status || null,
          jobId: filters.jobId || null,
          limitCount: 100
        });
        applicationsData = result.applications;
      }
      
      setApplications(applicationsData);
      
      // Calculate stats
      const statsData = applicationsData.reduce((acc, app) => {
        acc.total++;
        acc[app.status] = (acc[app.status] || 0) + 1;
        return acc;
      }, { total: 0, pending: 0, reviewing: 0, accepted: 0, rejected: 0 });
      
      setStats(statsData);
      
      // Extract unique jobs for filter dropdown
      const uniqueJobs = [...new Map(
        applicationsData
          .filter(app => app.jobDetails)
          .map(app => [app.jobDetails.id, app.jobDetails])
      ).values()];
      setJobs(uniqueJobs);
      
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Update application status
  const handleStatusUpdate = async (applicationId, newStatus, message = '') => {
    try {
      setUpdating(prev => ({ ...prev, [applicationId]: true }));
      
      await updateApplicationStatus(applicationId, newStatus, message, companyProfile.id);
      
      // Update local state
      setApplications(prev => prev.map(app => 
        app.id === applicationId 
          ? { ...app, status: newStatus, statusMessage: message, statusUpdatedAt: new Date() }
          : app
      ));
      
      // Update stats
      setStats(prev => {
        const newStats = { ...prev };
        const oldApp = applications.find(app => app.id === applicationId);
        if (oldApp) {
          newStats[oldApp.status] = Math.max(0, newStats[oldApp.status] - 1);
        }
        newStats[newStatus] = (newStats[newStatus] || 0) + 1;
        return newStats;
      });
      
    } catch (error) {
      console.error('Error updating application status:', error);
    } finally {
      setUpdating(prev => ({ ...prev, [applicationId]: false }));
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Get status configuration
  const getStatusConfig = (status) => {
    return applicationStatuses.find(s => s.value === status) || applicationStatuses[0];
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Application Card Component
  const ApplicationCard = ({ application }) => {
    const statusConfig = getStatusConfig(application.status);
    const isUpdating = updating[application.id];
    
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-start space-x-4">
            {/* Applicant Avatar */}
            <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
              {application.applicantDetails?.profilePhoto ? (
                <img 
                  src={application.applicantDetails.profilePhoto} 
                  alt={application.applicantDetails.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-6 h-6 text-gray-400" />
              )}
            </div>
            
            {/* Applicant Info */}
            <div>
              <h3 className="font-semibold text-gray-800">
                {application.applicantDetails?.name || 'Unknown Applicant'}
              </h3>
              <div className="text-sm text-gray-600 space-y-1">
                <div className="flex items-center space-x-2">
                  <GraduationCap className="w-4 h-4" />
                  <span>{application.applicantDetails?.university || 'University not specified'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>{application.applicantDetails?.email || 'Email not available'}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Status Badge */}
          <div className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${
            statusConfig.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
            statusConfig.color === 'blue' ? 'bg-blue-100 text-blue-800' :
            statusConfig.color === 'green' ? 'bg-green-100 text-green-800' :
            statusConfig.color === 'red' ? 'bg-red-100 text-red-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            <statusConfig.icon className="w-4 h-4" />
            <span>{statusConfig.label}</span>
          </div>
        </div>
        
        {/* Job Info */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-1">
            <Briefcase className="w-4 h-4 text-gray-500" />
            <span className="font-medium text-gray-800">
              {application.jobDetails?.title || 'Job title not available'}
            </span>
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center space-x-1">
              <Calendar className="w-4 h-4" />
              <span>Applied {formatDate(application.appliedAt)}</span>
            </div>
            {application.jobDetails?.location && (
              <div className="flex items-center space-x-1">
                <MapPin className="w-4 h-4" />
                <span>{application.jobDetails.location}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Application Materials */}
        <div className="mb-4">
          <div className="flex items-center space-x-4 text-sm">
            {application.cvUrl && (
              <button className="flex items-center space-x-1 text-green-600 hover:text-green-700">
                <FileText className="w-4 h-4" />
                <span>View CV</span>
                <ExternalLink className="w-3 h-3" />
              </button>
            )}
            {application.coverLetterUrl && (
              <button className="flex items-center space-x-1 text-green-600 hover:text-green-700">
                <FileText className="w-4 h-4" />
                <span>Cover Letter</span>
                <ExternalLink className="w-3 h-3" />
              </button>
            )}
            {application.portfolioUrl && (
              <button className="flex items-center space-x-1 text-green-600 hover:text-green-700">
                <Eye className="w-4 h-4" />
                <span>Portfolio</span>
                <ExternalLink className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
        
        {/* Cover Letter Preview */}
        {application.coverLetter && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Cover Letter</h4>
            <p className="text-sm text-gray-600 line-clamp-3">
              {application.coverLetter}
            </p>
          </div>
        )}
        
        {/* Status Message */}
        {application.statusMessage && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-medium text-blue-700 mb-1">Status Update</h4>
            <p className="text-sm text-blue-600">{application.statusMessage}</p>
            {application.statusUpdatedAt && (
              <p className="text-xs text-blue-500 mt-1">
                Updated {formatDate(application.statusUpdatedAt)}
              </p>
            )}
          </div>
        )}
        
        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button
            onClick={() => setSelectedApplication(application)}
            className="text-sm text-gray-600 hover:text-gray-800 flex items-center space-x-1"
          >
            <Eye className="w-4 h-4" />
            <span>View Details</span>
          </button>
          
          <div className="flex items-center space-x-2">
            {application.status === 'pending' && (
              <>
                <button
                  onClick={() => handleStatusUpdate(application.id, 'reviewing', 'Application is now under review')}
                  disabled={isUpdating}
                  className="px-3 py-1 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 disabled:opacity-50 flex items-center space-x-1"
                >
                  {isUpdating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                  <span>Review</span>
                </button>
                <button
                  onClick={() => handleStatusUpdate(application.id, 'rejected', 'Thank you for your interest. We have decided to move forward with other candidates.')}
                  disabled={isUpdating}
                  className="px-3 py-1 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center space-x-1"
                >
                  <X className="w-4 h-4" />
                  <span>Reject</span>
                </button>
              </>
            )}
            
            {application.status === 'reviewing' && (
              <>
                <button
                  onClick={() => handleStatusUpdate(application.id, 'accepted', 'Congratulations! We would like to move forward with your application.')}
                  disabled={isUpdating}
                  className="px-3 py-1 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center space-x-1"
                >
                  {isUpdating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  <span>Accept</span>
                </button>
                <button
                  onClick={() => handleStatusUpdate(application.id, 'rejected', 'Thank you for your time. We have decided to move forward with other candidates.')}
                  disabled={isUpdating}
                  className="px-3 py-1 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 disabled:opacity-50 flex items-center space-x-1"
                >
                  <X className="w-4 h-4" />
                  <span>Reject</span>
                </button>
              </>
            )}
            
            {(application.status === 'accepted' || application.status === 'rejected') && (
              <button
                onClick={() => handleStatusUpdate(application.id, 'reviewing', 'Status has been updated for further review')}
                disabled={isUpdating}
                className="px-3 py-1 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600 disabled:opacity-50 flex items-center space-x-1"
              >
                {isUpdating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
                <span>Re-review</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Application Management</h1>
          <p className="text-gray-600">
            Review and manage job applications for your posted positions
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600">Total Applications</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
            <div className="text-sm text-gray-600">Pending Review</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.reviewing}</div>
            <div className="text-sm text-gray-600">Under Review</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-green-600">{stats.accepted}</div>
            <div className="text-sm text-gray-600">Accepted</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
            <div className="text-sm text-gray-600">Rejected</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="p-4 border-b border-gray-200">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 text-gray-700 hover:text-gray-900"
            >
              <Filter className="w-5 h-5" />
              <span className="font-medium">Filters</span>
              {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
          
          {showFilters && (
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* Search */}
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search applicants..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                
                {/* Status Filter */}
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  {applicationStatuses.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
                
                {/* Job Filter */}
                <select
                  value={filters.jobId}
                  onChange={(e) => handleFilterChange('jobId', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">All Jobs</option>
                  {jobs.map(job => (
                    <option key={job.id} value={job.id}>
                      {job.title}
                    </option>
                  ))}
                </select>
                
                {/* University Filter */}
                <input
                  type="text"
                  placeholder="Filter by university..."
                  value={filters.university}
                  onChange={(e) => handleFilterChange('university', e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>
          )}
        </div>

        {/* Applications List */}
        {applications.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">No Applications Found</h3>
            <p className="text-gray-600">
              {filters.status || filters.searchTerm || filters.jobId ? 
                'No applications match your current filters.' :
                'You haven\'t received any job applications yet.'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {applications.map(application => (
              <ApplicationCard key={application.id} application={application} />
            ))}
          </div>
        )}

        {/* Application Detail Modal */}
        {selectedApplication && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-screen overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-800">Application Details</h2>
                  <button
                    onClick={() => setSelectedApplication(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {/* Detailed application view would go here */}
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Applicant Information</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p><strong>Name:</strong> {selectedApplication.applicantDetails?.name}</p>
                      <p><strong>Email:</strong> {selectedApplication.applicantDetails?.email}</p>
                      <p><strong>University:</strong> {selectedApplication.applicantDetails?.university}</p>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-2">Job Applied For</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p><strong>Position:</strong> {selectedApplication.jobDetails?.title}</p>
                      <p><strong>Applied:</strong> {formatDate(selectedApplication.appliedAt)}</p>
                    </div>
                  </div>
                  
                  {selectedApplication.coverLetter && (
                    <div>
                      <h3 className="font-semibold text-gray-800 mb-2">Cover Letter</h3>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="whitespace-pre-wrap">{selectedApplication.coverLetter}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationsManager;